<template>
  <transition name="modal-fade">
    <div v-if="visible" class="upgrade-overlay" @click.self="close">
      <div class="upgrade-modal" role="dialog" aria-modal="true">
        <button type="button" class="btn-close" title="Fechar" @click="close">
          ✕
        </button>

        <!-- Step 1: choose plan -->
        <template v-if="step === 'plans'">
          <div class="upgrade-header">
            <h2>{{ title }}</h2>
            <p class="subtitle">{{ subtitle }}</p>
            <div v-if="reason === 'ai_limit' && recap.length" class="ai-recap">
              <p>Este mês a IA já fez por você:</p>
              <ul>
                <li v-for="r in recap" :key="r.kind">✓ {{ r.label }}</li>
              </ul>
            </div>
            <p v-if="reason === 'ai_limit' && !isChange && perDayLabel" class="ai-limit-note">
              O Consultório custa {{ monthlyLabel }} por mês (cerca de <strong>{{ perDayLabel }} por dia</strong>) e libera
              até 50 ações de IA por dia, para ditar, organizar, ler fotos e exames e consultar o histórico. Com Pix, libera em segundos.
            </p>
          </div>

          <div v-if="loading" class="upgrade-loading">Carregando planos...</div>

          <div v-if="!loading && offer && showPlanList" class="welcome-offer">
            🎁 <strong>Oferta de boas-vindas:</strong> {{ offer.percent }}% de desconto no 1º mês dos planos mensais,
            até {{ formatOfferDate(offer.until) }}.
          </div>

          <div v-if="!loading && !showPlanList && chosenPlan" class="chosen-plan">
            <div class="chosen-info">
              <span class="chosen-name">{{ chosenPlan.name }}</span>
              <span v-if="offer && chosenPlan.interval_months !== 12" class="chosen-price">
                <strong>{{ formatPrice(Math.round(chosenPlan.price_cents * (100 - offer.percent) / 100)) }}</strong>
                no 1º mês, depois {{ formatPrice(chosenPlan.monthly_price_cents) }}/mês
              </span>
              <span v-else-if="chosenPlan.interval_months === 12" class="chosen-price">
                <strong>{{ formatPrice(chosenPlan.price_cents) }}</strong> por ano
              </span>
              <span v-else class="chosen-price"><strong>{{ formatPrice(chosenPlan.monthly_price_cents) }}</strong>/mês</span>
            </div>
            <button type="button" class="btn-link-change" @click="showPlanList = true">Trocar plano</button>
          </div>

          <div v-if="!loading && !showPlanList && chosenPlan && !isChange" class="keep-note">
            <p v-if="freeContrast">{{ freeContrast }}</p>
            <button v-if="yearlyAlternative" type="button" class="btn-link-change" @click="setInterval(12)">
              Prefere o anual? {{ formatPrice(yearlyAlternative.price_cents) }} por ano
              ({{ formatPrice(yearlyAlternative.monthly_price_cents) }}/mês)
            </button>
            <button v-else-if="chosenPlan.interval_months === 12" type="button" class="btn-link-change" @click="setInterval(1)">
              Prefere pagar por mês?
            </button>
          </div>

          <div v-if="!loading && hasYearly && showPlanList" class="interval-toggle" role="tablist">
            <button
              type="button"
              :class="{ active: interval === 1 }"
              @click="setInterval(1)"
            >
              Mensal
            </button>
            <button
              type="button"
              :class="{ active: interval === 12 }"
              @click="setInterval(12)"
            >
              Anual <span class="save">2 meses grátis</span>
            </button>
          </div>

          <div v-if="!loading && showPlanList" class="plans">
            <label
              v-for="plan in visiblePlans"
              :key="plan.identifier"
              class="plan"
              :class="{ selected: selectedPlan === plan.identifier }"
            >
              <input v-model="selectedPlan" type="radio" :value="plan.identifier" />
              <span class="plan-name">{{ plan.name }}</span>
              <span v-if="offer && plan.interval_months !== 12" class="plan-price">
                <s class="old-price">{{ formatPrice(plan.monthly_price_cents) }}</s>
                <strong>{{ formatPrice(Math.round(plan.price_cents * (100 - offer.percent) / 100)) }}</strong> no 1º mês
              </span>
              <span v-else class="plan-price">
                <strong>{{ formatPrice(plan.monthly_price_cents) }}</strong>/mês
              </span>
              <span v-if="offer && plan.interval_months !== 12" class="plan-billed">
                depois {{ formatPrice(plan.monthly_price_cents) }}/mês, sem fidelidade
              </span>
              <span v-if="plan.interval_months === 12" class="plan-billed">
                {{ formatPrice(plan.price_cents) }} cobrados por ano
              </span>
              <ul>
                <li><span class="check">✓</span> {{ customerTreatmentPlural }} ilimitados</li>
                <li>
                  <span class="check">✓</span> Até {{ plan.users_limit }} usuários
                </li>
                <li><span class="check">✓</span> 50 ações de IA por dia</li>
                <li><span class="check">✓</span> Transcrição e evolução com IA (SOAP, DAP, BIRP)</li>
              </ul>
            </label>
          </div>

          <form v-if="!loading" class="checkout-form" @submit.prevent="startCheckout">
            <p v-if="isChange" class="change-note">
              A troca vale na hora. A diferença de valor é ajustada proporcionalmente na próxima cobrança.
            </p>
            <label v-if="!hasCustomer" for="upgrade-cpf">CPF ou CNPJ do pagador</label>
            <input
              v-if="!hasCustomer"
              id="upgrade-cpf"
              v-model="cpfCnpj"
              v-maska
              data-maska="['###.###.###-##', '##.###.###/####-##']"
              inputmode="numeric"
              autocomplete="off"
              placeholder="000.000.000-00"
              required
            />
            <p v-if="error" class="error">{{ error }}</p>
            <button type="submit" class="btn-checkout" :disabled="requesting || !canSubmit">
              {{ requesting ? (isChange ? "Mudando..." : "Gerando pagamento...") : `${isChange ? "Mudar para" : "Assinar"} ${selectedPlanName}` }}
            </button>
            <p class="payment-methods">
              🔒
              Pague com <strong>PIX</strong> (liberação imediata) ou cartão.
              Sem fidelidade, cancele quando quiser.
            </p>
          </form>
        </template>

        <!-- Step 2: waiting for payment -->
        <template v-else-if="step === 'waiting'">
          <template v-if="pix">
            <div class="upgrade-header">
              <h2>Pague com Pix para liberar agora</h2>
              <p class="subtitle">
                Abra o app do seu banco e escaneie o QR Code, ou copie o código Pix.
                <strong v-if="pix.total">Valor: {{ pix.total }}.</strong> A liberação é automática em segundos.
              </p>
            </div>
            <div class="pix-box">
              <img v-if="pix.qrcode_url" :src="pix.qrcode_url" alt="QR Code Pix" class="pix-qr" />
              <button type="button" class="btn-checkout" @click="copyPix">{{ pixCopied ? "Código copiado!" : "Copiar código Pix" }}</button>
              <p class="pix-wait"><span class="dot"></span> Aguardando o pagamento...</p>
            </div>
            <div class="waiting-actions">
              <button type="button" class="btn-link" :disabled="requesting" @click="checkPayment(true)">
                {{ requesting ? "Verificando..." : "Já paguei, verificar agora" }}
              </button>
              <a :href="invoiceUrl" target="_blank" rel="noopener" class="btn-link small pay-card">Prefiro pagar com cartão</a>
            </div>
          </template>
          <template v-else>
            <div class="upgrade-header">
              <div class="waiting-icon">⏳</div>
              <h2>Conclua o pagamento</h2>
              <p class="subtitle">
                Abra a página de pagamento para pagar com PIX ou cartão. Seu plano é liberado
                automaticamente em segundos. Pode deixar esta janela aberta.
              </p>
            </div>
            <div class="waiting-actions">
              <a :href="invoiceUrl" target="_blank" rel="noopener" class="btn-checkout">
                Abrir página de pagamento
              </a>
              <button type="button" class="btn-link" :disabled="requesting" @click="checkPayment(true)">
                {{ requesting ? "Verificando..." : "Já paguei, verificar agora" }}
              </button>
            </div>
          </template>
          <div class="waiting-alt">
            <button type="button" class="btn-link small" :disabled="requesting" @click="discardPending">
              Escolher outro plano ou cancelar este pagamento
            </button>
          </div>
        </template>

        <!-- Step 3: success -->
        <template v-else-if="step === 'success'">
          <div class="upgrade-header">
            <div class="success-icon">✅</div>
            <h2>Plano {{ activePlanName }} ativado! 🎉</h2>
            <p v-if="changed" class="subtitle">
              Sua assinatura foi atualizada. A diferença de valor aparece na próxima cobrança.
            </p>
            <p v-else class="subtitle">
              Obrigado pela confiança. Você já pode cadastrar
              {{ customerTreatmentPlural.toLowerCase() }} sem limite e usar a IA todos os dias (até 50 ações de IA por dia).
            </p>
          </div>
          <div class="waiting-actions">
            <button type="button" class="btn-checkout" @click="finish">Continuar</button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
import { vMaska } from "maska/vue";
import billing, { formatPrice } from "@/utils/billing";
import { track } from "@/utils/analytics";

const POLL_INTERVAL = 6000;
const POLL_MAX_MS = 20 * 60 * 1000;

export default {
  name: "ComponentUpgradeModal",
  directives: { maska: vMaska },
  props: {
    visible: { type: Boolean, default: false },
    reason: { type: String, default: "" },
    limitMessage: { type: String, default: "" },
    preselect: { type: String, default: "lean" },
    // The caller already knows which plan the person wants: skip the plan list and go straight to payment.
    planKnown: { type: Boolean, default: false },
  },
  emits: ["close", "upgrade-success"],
  data() {
    return {
      step: "plans",
      loading: false,
      requesting: false,
      plans: [],
      selectedPlan: this.preselect,
      interval: 1,
      cpfCnpj: "",
      error: null,
      invoiceUrl: null,
      pix: null,
      pixCopied: false,
      activePlanName: "",
      pollTimer: null,
      pollStartedAt: null,
      offer: null,
      isChange: false,
      hasCustomer: false,
      changed: false,
      showPlanList: true,
      recap: [],
    };
  },
  computed: {
    customerTreatmentPlural() {
      return this.$store.getters["Companies/customerTreatmentPlural"] || "Pacientes";
    },
    hasYearly() {
      return this.plans.some((p) => p.interval_months === 12);
    },
    visiblePlans() {
      return this.plans.filter((p) => (p.interval_months || 1) === this.interval);
    },
    // Consultório mensal per day ("R$ 1,33"), the price of an AI that is always there.
    perDayLabel() {
      const plan = this.plans.find((p) => p.identifier === "lean");
      return plan ? formatPrice(Math.round(plan.price_cents / 30)) : "";
    },
    monthlyLabel() {
      const plan = this.plans.find((p) => p.identifier === "lean");
      return plan ? formatPrice(plan.price_cents) : "";
    },
    // The yearly twin of the chosen monthly plan, to show its monthly equivalent.
    yearlyAlternative() {
      if (!this.chosenPlan || this.chosenPlan.interval_months === 12) return null;
      return this.plans.find((p) => p.identifier === `${this.chosenPlan.identifier}_yearly`) || null;
    },
    // What changes if they do not subscribe, said concretely (only where the person is deciding).
    freeContrast() {
      if (!["trial", "welcome_offer", "ai_limit", "link"].includes(this.reason)) return "";
      return `Sem assinar, a conta continua no plano Grátis com seus dados salvos, até 50 ${this.customerTreatmentPlural.toLowerCase()} e 30 ações de IA por mês (cerca de 10 atendimentos ditados). Assinando, são até 50 ações de IA por dia.`;
    },
    chosenPlan() {
      return this.plans.find((p) => p.identifier === this.selectedPlan) || null;
    },
    selectedPlanName() {
      const plan = this.plans.find((p) => p.identifier === this.selectedPlan);
      return plan ? plan.name : "";
    },
    canSubmit() {
      if (!this.selectedPlan) return false;
      if (this.hasCustomer) return true;
      const digits = this.cpfCnpj.replace(/\D/g, "");
      return digits.length === 11 || digits.length === 14;
    },
    title() {
      if (this.reason === "customers_limit") return "Você chegou ao limite do plano Grátis";
      if (this.reason === "ai_limit")
        return (this.$store.state.Companies.company || {}).ai_quota_remaining
          ? "Continue usando a IA todos os dias"
          : "Suas ações de IA acabaram";
      if (this.reason === "payment_overdue") return "Regularize sua assinatura";
      if (this.reason === "users_limit") return "Traga sua equipe para o Ordexa";
      if (this.reason === "trial" || this.reason === "welcome_offer") return "Continue com tudo liberado";
      if (this.isChange) return "Mudar de plano";
      if (!this.showPlanList) return "Finalize sua assinatura";
      return "Escolha seu plano";
    },
    minutesSavedLabel() {
      const m = (this.$store.state.Companies.company || {}).ai_minutes_saved_total || 0;
      if (m < 15) return "";
      if (m < 60) return `${m} minutos`;
      const h = m / 60;
      return `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${h < 2 ? "hora" : "horas"}`;
    },
    // What the account did during the trial: patients, notes and AI time saved (only when there is something).
    usageText() {
      const u = (this.$store.state.Companies.company || {}).trial_usage;
      if (!u || !(u.patients || u.notes)) return "";
      const one = (this.$store.getters["Companies/customerTreatmentSingular"] || "paciente").toLowerCase();
      const parts = [];
      if (u.patients) parts.push(`${u.patients} ${u.patients === 1 ? one : this.customerTreatmentPlural.toLowerCase()}`);
      if (u.notes) parts.push(`${u.notes} ${u.notes === 1 ? "anotação" : "anotações"}`);
      let text = `Você já tem ${parts.join(" e ")} no Ordexa`;
      if (this.minutesSavedLabel) text += ` e a IA poupou cerca de ${this.minutesSavedLabel} de digitação`;
      return `${text}.`;
    },
    subtitle() {
      if (this.usageText && (this.reason === "trial" || this.reason === "welcome_offer") && !this.isChange)
        return `${this.usageText} Tudo fica salvo. Assinando, você continua sem limite de ${this.customerTreatmentPlural.toLowerCase()} e com a IA todos os dias (até 50 ações de IA por dia).`;
      if (this.reason === "payment_overdue")
        return "Não identificamos o pagamento da sua assinatura. Pague agora com Pix ou cartão e continue com tudo como está.";
      if (this.limitMessage) return this.limitMessage;
      if (this.minutesSavedLabel && !this.isChange)
        return `A IA já te poupou cerca de ${this.minutesSavedLabel} de digitação. Assinando, ela fica disponível todos os dias (até 50 ações de IA por dia), sem limite de ${this.customerTreatmentPlural.toLowerCase()}.`;
      if (this.reason === "ai_limit")
        return `No plano Grátis são 30 ações de IA por mês. Assinando, você tem até 50 por dia para transcrever áudios, organizar notas e analisar ${this.customerTreatmentPlural.toLowerCase()}.`;
      if (this.reason === "trial")
        return `Seus dados ficam salvos. Assinando, você continua sem limite de ${this.customerTreatmentPlural.toLowerCase()} e com a IA todos os dias (até 50 ações de IA por dia).`;
      return "Todos os planos incluem prontuário, agenda, tarefas, relatórios e IA.";
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.open();
        } else {
          this.stopPolling();
          document.body.style.overflow = "";
        }
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
    document.body.style.overflow = "";
  },
  methods: {
    formatPrice,

    formatOfferDate(value) {
      return new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    },

    // What the AI did this month (counts only), so the decision is about the routine it already takes care of.
    async loadRecap() {
      try {
        const { data } = await this.axios.get("/api/v1/ai_tasks/recap", { skipGlobalErrorHandler: true });
        this.recap = (data && data.items) || [];
      } catch (e) {
        this.recap = [];
      }
    },
    async open() {
      document.body.style.overflow = "hidden";
      this.recap = [];
      if (this.reason === "ai_limit") this.loadRecap();
      this.error = null;
      this.loading = true;
      this.selectedPlan = this.preselect || "lean";
      this.interval = this.selectedPlan.endsWith("_yearly") ? 12 : 1;

      try {
        const data = await billing.get();
        this.plans = data.plans;
        this.offer = data.welcome_offer || null;
        this.isChange = !!data.active_subscription;
        this.hasCustomer = !!data.has_billing_customer;
        this.changed = false;
        this.showPlanList = !(this.planKnown && this.plans.some((p) => p.identifier === this.selectedPlan));

        if (data.pending && data.pending.invoice_url) {
          this.selectedPlan = data.pending.plan_identifier;
          this.interval = this.selectedPlan.endsWith("_yearly") ? 12 : 1;
          this.invoiceUrl = data.pending.invoice_url;
          this.pix = await billing.pix().catch(() => null);
          this.step = "waiting";
          this.startPolling();
        } else {
          this.step = "plans";
        }
      } catch (e) {
        this.error = "Não foi possível carregar os planos. Tente novamente.";
      } finally {
        this.loading = false;
      }
      track("view_plans", { reason: this.reason || "manual" });
    },

    setInterval(months) {
      this.interval = months;
      const base = this.selectedPlan.replace("_yearly", "");
      this.selectedPlan = months === 12 ? `${base}_yearly` : base;
    },

    async startCheckout() {
      if (!this.canSubmit) return;

      this.requesting = true;
      this.error = null;
      track("checkout_submit", { plan: this.selectedPlan, reason: this.reason || "manual" });

      try {
        const data = await billing.checkout(this.selectedPlan, this.hasCustomer ? null : this.cpfCnpj);
        this.$store.dispatch("Companies/refreshCompany", data.company);

        if (data.plan_changed) {
          this.changed = true;
          this.activePlanName = data.plan.name;
          this.step = "success";
          return;
        }

        const plan = this.plans.find((p) => p.identifier === this.selectedPlan);
        track("begin_checkout", {
          currency: "BRL",
          value: plan ? plan.price_cents / 100 : 0, // full charge (yearly = 12 months)
          items: [{ item_id: this.selectedPlan }],
        });

        this.invoiceUrl = data.invoice_url;
        this.pix = data.pix || null;
        this.step = "waiting";
        this.startPolling();
      } catch (e) {
        track("checkout_error", { status: (e.response && e.response.status) || 0 });
        this.error =
          (e.response && e.response.data && e.response.data.error) ||
          "Não foi possível iniciar o pagamento. Tente novamente ou fale com suporte@ordexa.com.br.";
      } finally {
        this.requesting = false;
      }
    },

    async copyPix() {
      try {
        await navigator.clipboard.writeText(this.pix.qrcode_text);
        this.pixCopied = true;
        track("pix_copy_checkout");
        setTimeout(() => (this.pixCopied = false), 3000);
      } catch (e) {
        this.pixCopied = false;
      }
    },

    async discardPending() {
      this.requesting = true;
      try {
        await billing.discardPending();
        this.stopPolling();
        this.invoiceUrl = null;
        this.pix = null;
        this.step = "plans";
      } catch (e) {
        this.error = "Não foi possível cancelar este pagamento agora. Tente de novo.";
      } finally {
        this.requesting = false;
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollStartedAt = Date.now();
      this.pollTimer = setInterval(() => this.checkPayment(false), POLL_INTERVAL);
    },

    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    async checkPayment(manual) {
      if (!manual && Date.now() - this.pollStartedAt > POLL_MAX_MS) {
        this.stopPolling();
        return;
      }
      if (manual) this.requesting = true;

      try {
        const data = await billing.sync();
        this.$store.dispatch("Companies/refreshCompany", data.company);

        if (!data.pending && data.plan && data.plan.identifier !== "free") {
          this.stopPolling();
          this.activePlanName = data.plan.name;
          this.step = "success";
          track("purchase", {
            currency: "BRL",
            value: data.plan.price_cents / 100,
            transaction_id: `${data.company.id}-${data.plan.identifier}-${Date.now()}`,
            items: [{ item_id: data.plan.identifier }],
            reason: this.reason || "manual",
          });
        } else if (manual) {
          this.$eventbus.emit("ComponentToast/add", {
            autoHide: true,
            content: "Ainda não recebemos a confirmação. PIX costuma levar poucos segundos.",
            type: "info",
          });
        }
      } catch (e) {
        // keep polling silently
      } finally {
        this.requesting = false;
      }
    },

    finish() {
      this.close();
    },

    // After a payment, closing (✕, outside click or "Continuar") still resumes what was waiting on it.
    close() {
      this.stopPolling();
      if (this.step === "success") this.$emit("upgrade-success");
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.keep-note {
  margin: 0 0 12px;
  font-size: 0.88em;
  color: #555;
  line-height: 1.45;
}
.keep-note .btn-link-change {
  white-space: normal;
  text-align: left;
  padding: 0;
}
.keep-note p {
  margin: 0 0 4px;
}
.ai-recap {
  margin: 10px 0 0;
  padding: 10px 14px;
  background: #f1fafc;
  border: 1px solid #d6eef4;
  border-radius: 10px;
  font-size: 0.92em;
  color: #1a5f6b;
}
.ai-recap p {
  margin: 0 0 4px;
  font-weight: 600;
}
.ai-recap ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ai-recap li {
  padding: 2px 0;
}
.ai-limit-note {
  margin: 8px 0 0;
  font-size: 0.92em;
  color: #3d5056;
}
.pay-card {
  display: block;
  text-align: center;
  margin-top: 4px;
}
.pix-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 8px 0 14px;
}
.pix-qr {
  width: 220px;
  height: 220px;
  max-width: 70vw;
  max-height: 70vw;
  border: 1px solid #e1ecef;
  border-radius: 12px;
  padding: 8px;
  background: #fff;
}
.pix-wait {
  margin: 0;
  color: #5f6f75;
  font-size: 0.9em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pix-wait .dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #29b1cc;
  animation: pixpulse 1.2s ease-in-out infinite;
}
@keyframes pixpulse {
  50% {
    opacity: 0.25;
  }
}
.upgrade-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 30, 40, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}

.upgrade-modal {
  position: relative;
  background: #fff;
  border-radius: 14px;
  max-width: 640px;
  width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 2em 2em 1.5em;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.btn-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.2em;
  color: #999;
  cursor: pointer;
  padding: 8px;
}

.upgrade-header {
  text-align: center;
  margin-bottom: 1.5em;
}

.upgrade-header h2 {
  padding: 0 1.2em;
  font-size: 1.4em;
  font-weight: 700;
  color: #222;
  margin: 0 0 0.4em;
}

.subtitle {
  color: #666;
  font-size: 0.95em;
  line-height: 1.5;
  margin: 0;
}

.upgrade-loading {
  text-align: center;
  color: #888;
  padding: 2em 0;
}

.plans {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1em;
}

.plan {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 2px solid #e3e7ea;
  border-radius: 10px;
  padding: 1.2em 1em 1em;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.plan input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.plan.selected {
  border-color: #29b1cc;
  box-shadow: 0 0 0 3px rgba(41, 177, 204, 0.15);
}

.plan-badge {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  background: #40ad54;
  color: #fff;
  font-size: 0.7em;
  font-weight: 700;
  text-transform: uppercase;
  padding: 0.2em 0.9em;
  border-radius: 10px;
  white-space: nowrap;
}

.plan-name {
  font-weight: 700;
  font-size: 1.1em;
  color: #222;
}

.plan-price {
  color: #666;
  margin: 0.3em 0 0.8em;
}

.plan-price strong {
  font-size: 1.5em;
  color: #222;
}

.plan ul {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.88em;
  color: #555;
}

.plan li {
  padding: 0.2em 0;
}

.plan li .check {
  color: #40ad54;
  margin-right: 0.4em;
}

.interval-toggle {
  display: flex;
  justify-content: center;
  gap: 0.3em;
  background: #eef3f5;
  border-radius: 999px;
  padding: 0.3em;
  width: fit-content;
  margin: 0 auto 1.4em;
}

.interval-toggle button {
  border: none;
  background: transparent;
  border-radius: 999px;
  padding: 0.5em 1.2em;
  font-weight: 600;
  color: #557;
  cursor: pointer;
}

.interval-toggle button.active {
  background: #fff;
  color: #1a8ea8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.interval-toggle .save {
  font-size: 0.75em;
  color: #2c8a40;
  margin-left: 0.3em;
}

.change-note {
  background: #f4f7f8;
  border-radius: 8px;
  padding: 0.6em 0.8em;
  font-size: 0.88em;
  color: #4a5b60;
  margin: 0 0 0.8em;
}

.waiting-alt {
  text-align: center;
  margin-top: 0.6em;
}

.btn-link.small {
  font-size: 0.85em;
  color: #6b7d82;
}

.welcome-offer {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  border-radius: 10px;
  padding: 0.7em 1em;
  margin-bottom: 1em;
  font-size: 0.92em;
}

.old-price {
  color: #999;
  font-size: 0.85em;
  margin-right: 0.3em;
}

.plan-billed {
  font-size: 0.8em;
  color: #888;
  margin: -0.6em 0 0.8em;
}

.checkout-form {
  margin-top: 1.5em;
}

.chosen-plan {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 2px solid #29b1cc;
  border-radius: 10px;
  padding: 0.9em 1em;
  background: rgba(41, 177, 204, 0.06);
}
.chosen-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.chosen-name {
  font-weight: 700;
  font-size: 1.1em;
  color: #222;
}
.chosen-price {
  color: #555;
  font-size: 0.92em;
}
.chosen-price strong {
  color: #222;
  font-size: 1.15em;
}
.btn-link-change {
  background: none;
  border: none;
  color: #1a8ea8;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
  padding: 6px;
}

.checkout-form label {
  display: block;
  font-size: 0.85em;
  font-weight: 600;
  color: #444;
  margin-bottom: 0.4em;
}

.checkout-form input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.8em 1em;
  border: 1px solid #ccd3d8;
  border-radius: 8px;
  font-size: 1em;
}

.checkout-form input:focus {
  outline: none;
  border-color: #29b1cc;
}

.error {
  color: #d73641;
  font-size: 0.9em;
  margin: 0.6em 0 0;
}

.btn-checkout {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin-top: 1em;
  background: #29b1cc;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.95em;
  font-size: 1.05em;
  font-weight: 700;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}

.btn-checkout:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-link {
  background: none;
  border: none;
  color: #1a8ea8;
  font-weight: 600;
  margin-top: 1em;
  cursor: pointer;
  width: 100%;
}

.payment-methods,
.hint {
  text-align: center;
  font-size: 0.82em;
  color: #888;
  margin: 0.9em 0 0;
}

.waiting-icon,
.success-icon {
  font-size: 2.6em;
  margin-bottom: 0.3em;
}

.waiting-icon {
  color: #f0a020;
}

.success-icon {
  color: #40ad54;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .upgrade-modal {
    padding: 1.6em 1.1em 1.2em;
  }

  .plans {
    grid-template-columns: 1fr;
    gap: 1.2em;
  }

  /* keep the pay button reachable without scrolling */
  .checkout-form {
    position: sticky;
    bottom: -1.2em;
    margin: 1em -1.1em -1.2em;
    padding: 0.8em 1.1em 1.2em;
    background: #fff;
    box-shadow: 0 -6px 12px rgba(0, 0, 0, 0.06);
  }
  .checkout-form .btn-checkout {
    margin-top: 0.6em;
  }
  .checkout-form .payment-methods {
    margin-top: 0.6em;
  }
}
</style>
